import {
	Divider,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { PlusIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AddGoalCta } from "web_myplann/Goals/Components/AddGoalCta";
import { CompletedGoalListItem } from "web_myplann/Goals/Components/CompletedGoalListItem";
import { CompletedGoalListItemSkeleton } from "web_myplann/Goals/Components/CompletedGoalListItemSkeleton";
import { GoalListItem } from "web_myplann/Goals/Components/GoalListItem";
import { GoalListItemSkeleton } from "web_myplann/Goals/Components/GoalListItemSkeleton";
import { GoalsEmptyState } from "web_myplann/Goals/Components/GoalsEmptyState";
import { useActiveGoals } from "web_myplann/Goals/Hooks/useActiveGoals";
import { useCompletedGoals } from "web_myplann/Goals/Hooks/useCompletedGoals";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

export const GoalsListPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { goals: activeGoals, isPending: isActivePending } = useActiveGoals();
	const { goals: completedGoals, isPending: isCompletedPending } =
		useCompletedGoals();

	const isLoading = isActivePending || isCompletedPending;
	const isEmpty =
		!isLoading && activeGoals.length === 0 && completedGoals.length === 0;

	const handleAddGoal = () => {
		navigate(getRoute(Routing.Goals.GoalFormCreate));
	};

	const ctaTitle =
		activeGoals.length === 0 && completedGoals.length > 0
			? t(Resources.Goals.List.ctaTitleCompleted)
			: t(Resources.Goals.List.ctaTitle);

	if (isEmpty) {
		return (
			<MyPlannLayout
				disableContentPadding
				disableTopPadding
				topBarBgColor="transparent"
				overlayTopBar
				topBar={
					<NavigationHeader
						title={
							<Typography
								variant="subtitle3"
								color={theme.palette.darkmodeText.white}
							>
								{t(Resources.Goals.List.title)}
							</Typography>
						}
						bgColor="transparent"
					/>
				}
			>
				<GoalsEmptyState />
			</MyPlannLayout>
		);
	}

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Goals.List.title)}
					endComponent={
						<IconButton variant="ghost" onClick={handleAddGoal}>
							<PlusIcon size={16} />
						</IconButton>
					}
					bgColor={theme.palette.background.paper}
				/>
			}
		>
			<NativePullToRefresh>
				<Stack pt={1}>
					<CollapsibleSection title={t(Resources.Goals.List.activeGoals)}>
						<Stack
							bgcolor={theme.palette.background.paper}
							divider={
								<Divider sx={{ borderColor: theme.palette.grey[100] }} />
							}
						>
							{isActivePending ? (
								<>
									<GoalListItemSkeleton />
									<GoalListItemSkeleton />
									<GoalListItemSkeleton />
								</>
							) : (
								activeGoals.map((goal) => (
									<GoalListItem key={goal.id} goal={goal} />
								))
							)}
						</Stack>
					</CollapsibleSection>

					{(isCompletedPending || completedGoals.length > 0) && (
						<Stack pt={1.2}>
							<CollapsibleSection
								title={
									isCompletedPending
										? t(Resources.Goals.List.completed)
										: `${t(Resources.Goals.List.completed)} (${completedGoals.length})`
								}
								expandable
							>
								<Stack
									bgcolor={theme.palette.background.paper}
									divider={
										<Divider
											sx={{
												mx: 1.6,
												borderColor: theme.palette.grey[100],
											}}
										/>
									}
								>
									{isCompletedPending ? (
										<CompletedGoalListItemSkeleton />
									) : (
										completedGoals.map((goal) => (
											<CompletedGoalListItem key={goal.id} goal={goal} />
										))
									)}
								</Stack>
							</CollapsibleSection>
						</Stack>
					)}

					<AddGoalCta title={ctaTitle} onClick={handleAddGoal} />
				</Stack>
			</NativePullToRefresh>
		</MyPlannLayout>
	);
};
